import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Select, Button, List, Space, Input, Alert, Typography, Spin, Calendar, Tag, Divider, message, Tooltip } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { batchActions } from '../actions/batchActions';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { getTenantId } from '../services/helper';

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);

const { Text } = Typography;
const { Option } = Select;

const normalizeAssignedDateValue = (assignedDate: any) => {
  if (!assignedDate) return null;
  if (typeof assignedDate === 'object' && !Array.isArray(assignedDate)) {
    return assignedDate.date || assignedDate.assignedDate || assignedDate.value || assignedDate.day || null;
  }
  return assignedDate;
};

const getAssignmentDates = (item: any) => {
  const values = Array.isArray(item?.assignedDates)
    ? item.assignedDates
    : (item?.assignedDate ? [item.assignedDate] : (item?.studentId?.assignedDates ? item.studentId.assignedDates : []));
  return (values || []).map((dateValue: any) => normalizeAssignedDateValue(dateValue)).filter(Boolean);
};

const getAssignmentDateKey = (assignedDate: any) => {
  const dateValue = normalizeAssignedDateValue(assignedDate);
  const parsed = dayjs(dateValue);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

interface StudentDatePickerProps {
  batchId: string;
  startTime: string;
  endTime: string;
  isOnRoad?: boolean;
  assignedStudents?: any[];
  layout?: 'vertical' | 'horizontal';
  onStudentDateAdd: (student: any, assignedDate: string) => void;
  onStudentRemove?: (studentId: string, assignedDate?: string) => void;
  maxStudentsPerDay?: number;
}

const StudentDatePicker: React.FC<StudentDatePickerProps> = ({
  batchId,
  startTime,
  endTime,
  isOnRoad = false,
  assignedStudents = [],
  layout = 'vertical',
  onStudentDateAdd,
  onStudentRemove,
  maxStudentsPerDay = 3,
}) => {
  const dispatch = useDispatch();
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [calendarMonth, setCalendarMonth] = useState<Dayjs>(dayjs());

  const availableStudentsState = useSelector((state: any) => state.getAvailableStudentsForBatchReducer);
  const viewBatchState = useSelector((state: any) => state.viewBatchReducer);
  const isLoading = availableStudentsState?.isLoading;

  const availableStudentsRaw = (availableStudentsState?.availableStudents?.users || availableStudentsState?.availableStudents || []) as any[];
  const batchDetails = viewBatchState?.batchData?.batch || viewBatchState?.batchData || null;

  const getStudentId = (student: any) => {
    if (!student) return '';
    if (student.studentId) {
      if (typeof student.studentId === 'object' && student.studentId._id) {
        return String(student.studentId._id);
      }
      if (typeof student.studentId === 'string' || typeof student.studentId === 'number') {
        return String(student.studentId);
      }
    }
    if (student._id) return String(student._id);
    return String(student);
  };



  const getStudentDisplayName = (assignment: any): string => {
    if (assignment.firstName) {
      return `${assignment.firstName} ${assignment.lastName || ''}`.trim();
    }
    if (assignment.studentId && typeof assignment.studentId === 'object') {
      const s = assignment.studentId;
      if (s.firstName) return `${s.firstName} ${s.lastName || ''}`.trim();
    }
    return 'Student';
  };

  const assignedStudentMap = useMemo(() => {
    const map = new Map<string, { count: number; dates: Dayjs[] }>();
    assignedStudents.forEach((entry: any) => {
      const studentId = getStudentId(entry);
      if (!studentId) return;

      const stats = map.get(studentId) || { count: 0, dates: [] };

      const dates = getAssignmentDates(entry);
      (dates || []).forEach((d: any) => {
        const dateObj = dayjs(d);
        if (dateObj.isValid()) {
          stats.dates.push(dateObj);
          stats.count += 1;
        }
      });

      map.set(studentId, stats);
    });
    return map;
  }, [assignedStudents]);

  const studentCapacityMap = useMemo(() => {
    const map = new Map<string, number>();
    availableStudentsRaw.forEach((student: any) => {
      const studentId = getStudentId(student);
      if (!studentId) return;
      const assignedCountAtFetch = assignedStudentMap.get(studentId)?.count || 0;
      const remainingAtFetch = student.enrollment?.classesRemaining ?? 0;
      map.set(studentId, remainingAtFetch + assignedCountAtFetch);
    });
    return map;
  }, [availableStudentsRaw]);

  const availableStudents = useMemo(() => {
    return availableStudentsRaw.filter((student: any) => {
      const studentId = getStudentId(student);
      if (!studentId) return false;
      const assignedCount = assignedStudentMap.get(studentId)?.count || 0;
      const remainingClasses = student.enrollment?.classesRemaining ?? 0;
      const hasExistingAssignments = assignedCount > 0;
      return student.canAssign !== false && (hasExistingAssignments || assignedCount < remainingClasses || studentId === selectedStudentId);
    });
  }, [availableStudentsRaw, assignedStudentMap, selectedStudentId]);

  const selectedStudent = useMemo(() => {
    return (
      availableStudents.find((student: any) => getStudentId(student) === selectedStudentId) ||
      availableStudentsRaw.find((student: any) => getStudentId(student) === selectedStudentId) ||
      null
    );
  }, [availableStudents, availableStudentsRaw, selectedStudentId]);

  useEffect(() => {
    if (!batchId || !startTime || !endTime) return;
    const assignedDate = (selectedDate || dayjs()).format('YYYY-MM-DD');
    dispatch(batchActions.getAvailableStudentsForBatch({
      batchId,
      startTime,
      endTime,
      assignedDate,
      searchText,
      isOnRoad,
    }));
  }, [batchId, startTime, endTime, searchText, dispatch]);

  const handleStudentSelect = (studentId: string) => {
    setSelectedStudentId(studentId);
    setSelectedDate(null);
  };

  const parseTime = (timeValue: string) => {
    if (!timeValue) return null;
    const [hours, minutes] = timeValue.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    return hours * 60 + minutes;
  };

  const selectedStudentAssignedDates = useMemo(() => {
    if (!selectedStudentId) return [];
    return assignedStudents.reduce((dates: Dayjs[], entry: any) => {
      if (getStudentId(entry) !== selectedStudentId) return dates;
      const entryDates = getAssignmentDates(entry);
      (entryDates || []).forEach((d: any) => {
        const dateObj = dayjs(d);
        if (dateObj.isValid()) {
          dates.push(dateObj);
        }
      });
      return dates;
    }, [] as Dayjs[]);
  }, [assignedStudents, selectedStudentId]);

  const selectedStudentAssignedCount = selectedStudentAssignedDates.length;

  const canRemoveAssignmentDate = (date: Dayjs) => {
    const today = dayjs().startOf('day');
    if (date.isBefore(today)) return false;

    if (date.isSame(today, 'day')) {
      const currentMinutes = dayjs().hour() * 60 + dayjs().minute();
      const batchStartMinutes = parseTime(startTime);
      if (batchStartMinutes !== null && currentMinutes >= batchStartMinutes) {
        return false;
      }
    }

    return true;
  };

  const isCurrentBatchAssignmentForSelectedStudent = (date: Dayjs) => {
    if (!selectedStudentId) return false;
    const dateKey = date.format('YYYY-MM-DD');
    return (assignedStudents || []).some((entry: any) => {
      // Must match the selected student
      if (getStudentId(entry) !== selectedStudentId) return false;
      return (getAssignmentDates(entry) || []).some((assignedDate: any) => {
        const parsedDate = dayjs(assignedDate);
        return parsedDate.isValid() && parsedDate.format('YYYY-MM-DD') === dateKey;
      });
    });
  };

  const isSameBatchAssignmentEntry = (entry: any) => {
    const entryBatchId = entry?.batchId || entry?.batch?._id || entry?.batch?.id || entry?.assignedBatchId || entry?.batchData?._id;
    return Boolean(entryBatchId) && String(entryBatchId) === String(batchId);
  };

  const hasSameDateTimeAssignment = (date: Dayjs) => {
    if (!selectedStudent?.enrollment?.assignedDates || !startTime || !endTime) {
      return false;
    }

    const selectedStart = parseTime(startTime);
    const selectedEnd = parseTime(endTime);
    if (selectedStart === null || selectedEnd === null) {
      return false;
    }

    const dateKey = date.format('YYYY-MM-DD');
    return (selectedStudent.enrollment.assignedDates || []).some((entry: any) => {
      const entryDate = normalizeAssignedDateValue(entry?.date || entry?.assignedDate || entry?.value || entry?.day);
      const parsedDate = dayjs(entryDate);
      if (!parsedDate.isValid()) {
        return false;
      }
      const entryDateKey = parsedDate.format('YYYY-MM-DD');
      if (entryDateKey !== dateKey) {
        return false;
      }
      if (isSameBatchAssignmentEntry(entry)) {
        return false;
      }
      const entryStart = parseTime(entry?.startTime || entry?.stime || entry?.start || null);
      const entryEnd = parseTime(entry?.endTime || entry?.etime || entry?.end || null);
      if (entryStart === null || entryEnd === null) {
        return false;
      }
      return selectedStart < entryEnd && entryStart < selectedEnd;
    });
  };

  const hasOtherBatchConflict = useMemo(() => {
    if (!selectedStudent?.conflicts) {
      return false;
    }

    return selectedStudent.conflicts.some((conflict: any) => {
      const conflictBatchId = conflict?.batchId || conflict?.batch?._id || conflict?.batch?.id || conflict?.assignedBatchId || null;
      return !conflictBatchId || String(conflictBatchId) !== String(batchId);
    });
  }, [batchId, selectedStudent]);

  const selectedStudentAssignedDateKeys = useMemo(
    () => new Set(selectedStudentAssignedDates.map((date) => date.format('YYYY-MM-DD'))),
    [selectedStudentAssignedDates]
  );

  const assignmentsByDate = useMemo(() => {
    const map = new Map<string, any[]>();
    assignedStudents.forEach((entry: any) => {
      const dates = getAssignmentDates(entry);
      (dates || []).forEach((d: any) => {
        const dateObj = dayjs(d);
        if (!dateObj.isValid()) return;
        const dateKey = dateObj.format('YYYY-MM-DD');
        const existing = map.get(dateKey) || [];
        existing.push(entry);
        map.set(dateKey, existing);
      });
    });
    return map;
  }, [assignedStudents]);

  const selectedStudentAssignedDateSet = selectedStudentAssignedDateKeys;
  const isAssignedToSelectedStudent = (date: Dayjs) => {
    if (selectedStudentAssignedDateSet.has(date.format('YYYY-MM-DD'))) return true;
    return isCurrentBatchAssignmentForSelectedStudent(date);
  };
  const isAssignedToOtherStudent = (date: Dayjs) => {
    const key = date.format('YYYY-MM-DD');
    if (!assignmentsByDate.has(key)) return false;
    return assignmentsByDate.get(key)?.some((entry: any) => getStudentId(entry) !== selectedStudentId) ?? false;
  };

  const isHorizontalLayout = layout === 'horizontal';

  const handleDateClick = (date: Dayjs) => {
    if (!selectedStudentId || !selectedStudent) return;

    if (isAssignedToSelectedStudent(date)) {
      if (!canRemoveAssignmentDate(date)) {
        message.warning('Past or already started assignments cannot be removed');
        return;
      }
      if (onStudentRemove) {
        onStudentRemove(selectedStudentId, date.format('YYYY-MM-DD'));
      }
      return;
    }

    if (isDateBlocked(date)) {
      if (hasOtherBatchTimeConflictOnDate(date)) {
        message.error('Student is already assigned to an overlapping time slot in another batch on this date');
      } else if (hasSameDateTimeAssignment(date)) {
        message.error('Student is already assigned to this date and time in another batch');
      } else if (hasOtherBatchConflict) {
        message.error('Student has scheduling conflicts and cannot be assigned to this slot');
      } else if (selectedStudent?.enrollment?.classesRemaining <= 0) {
        message.error('Student has no remaining classes in the current enrollment');
      } else {
        message.warning('This student cannot be assigned to the selected date');
      }
      return;
    }

    setSelectedDate(date);
    onStudentDateAdd(selectedStudent, date.format('YYYY-MM-DD'));
  };

  const hasOtherBatchTimeConflictOnDate = (date: Dayjs): boolean => {
    if (!startTime || !endTime) return false;
    const selectedStart = parseTime(startTime);
    const selectedEnd = parseTime(endTime);
    if (selectedStart === null || selectedEnd === null) return false;
    const dateKey = date.format('YYYY-MM-DD');

    const overlapsTime = (entry: any): boolean => {
      const entryStart = parseTime(entry?.startTime || entry?.stime || entry?.start || null);
      const entryEnd = parseTime(entry?.endTime || entry?.etime || entry?.end || null);
      if (entryStart === null || entryEnd === null) return false;
      return selectedStart < entryEnd && entryStart < selectedEnd;
    };

    const matchesDate = (entry: any): boolean => {
      const entryDate = normalizeAssignedDateValue(
        entry?.date || entry?.assignedDate || entry?.value || entry?.day
      );
      const parsedDate = dayjs(entryDate);
      return parsedDate.isValid() && parsedDate.format('YYYY-MM-DD') === dateKey;
    };

    const enrollmentDates = selectedStudent?.enrollment?.assignedDates || [];
    const conflictInEnrollment = enrollmentDates.some((entry: any) => {
      if (isSameBatchAssignmentEntry(entry)) return false;
      return matchesDate(entry) && overlapsTime(entry);
    });
    if (conflictInEnrollment) return true;

    const studentAssignedDates = selectedStudent?.assignedDates || [];
    const conflictInStudentDates = studentAssignedDates.some((entry: any) => {
      if (isSameBatchAssignmentEntry(entry)) return false;
      return matchesDate(entry) && overlapsTime(entry);
    });
    if (conflictInStudentDates) return true;

    const conflicts = selectedStudent?.conflicts || [];
    const conflictInConflicts = conflicts.some((conflict: any) => {
      const conflictBatchId = conflict?.batchId || conflict?.batch?._id || conflict?.batch?.id || conflict?.assignedBatchId || null;
      if (conflictBatchId && String(conflictBatchId) === String(batchId)) return false;

      const conflictDates = conflict?.dates || conflict?.assignedDates || (conflict?.date ? [conflict] : []);
      return conflictDates.some((entry: any) => matchesDate(entry) && overlapsTime(entry));
    });
    if (conflictInConflicts) return true;

    return false;
  };

  const isDateBlocked = (date: Dayjs) => {
    const today = dayjs().startOf('day');
    if (date.isBefore(today)) return true;

    const currentMinutes = dayjs().hour() * 60 + dayjs().minute();
    const batchStartMinutes = parseTime(startTime);
    if (date.isSame(today, 'day') && batchStartMinutes !== null && currentMinutes >= batchStartMinutes) {
      return true;
    }

    if (selectedStudentId && isCurrentBatchAssignmentForSelectedStudent(date)) {
      return !canRemoveAssignmentDate(date);
    }

    const dateKey = date.format('YYYY-MM-DD');
    const assignmentsForDate = assignmentsByDate.get(dateKey) || [];
    const uniqueStudentIds = new Set(assignmentsForDate.map((entry: any) => getStudentId(entry)));
    const selectedAlreadyOnDate = selectedStudentId ? uniqueStudentIds.has(selectedStudentId) : false;
    if (!selectedAlreadyOnDate && uniqueStudentIds.size >= maxStudentsPerDay) {
      return true;
    }

    if (!selectedStudentId) {
      return false;
    }

    // Block if student has overlapping time in another batch on this date
    if (hasOtherBatchTimeConflictOnDate(date)) {
      return true;
    }

    if (hasOtherBatchConflict) {
      return true;
    }

    const remainingClasses = selectedStudentId
      ? (studentCapacityMap.get(selectedStudentId) ?? (selectedStudent?.enrollment?.classesRemaining ?? 0)) - selectedStudentAssignedCount + (isAssignedToSelectedStudent(date) ? 0 : 0)
      : (selectedStudent?.enrollment?.classesRemaining ?? 0);
    if (remainingClasses <= 0) {
      return true;
    }

    if (!isAssignedToSelectedStudent(date) && selectedStudentAssignedCount >= (studentCapacityMap.get(selectedStudentId || '') ?? (selectedStudent?.enrollment?.classesRemaining ?? 0))) {
      return true;
    }

    return false;
  };

  const selectedStudentDateList = useMemo(() => {
    if (!selectedStudentId) return [];
    return (selectedStudentAssignedDates || [])
      .map((date: any) => (date && dayjs(date).isValid() ? dayjs(date) : null))
      .filter((date): date is Dayjs => Boolean(date))
      .sort((a, b) => a.valueOf() - b.valueOf())
      .map((date) => ({
        key: date.format('YYYY-MM-DD'),
        label: date.format('DD/MM/YYYY'),
        value: date
      }));
  }, [selectedStudentAssignedDates, selectedStudentId]);

  return (
    <div className="student-date-picker">
      <div style={{ marginBottom: 16 }}>
        <Text strong>Students available for this time slot</Text>
        <br />
        <Text type="secondary">Select a student, then click dates to assign. Assigned dates remain highlighted and can be removed from the list below.</Text>
      </div>

      {/* Horizontal layout is reversed so the calendar sits on the left and the
          student list on the right — matching the batch view's read-only layout. */}
      <div style={{ marginTop: 16, display: 'flex', flexDirection: isHorizontalLayout ? 'row-reverse' : 'column', gap: 16, alignItems: isHorizontalLayout ? 'flex-start' : 'stretch' }}>
        <div className='edit-student-list' style={{ width: isHorizontalLayout ? '40%' : '100%' }}>
          {/* Search box */}
          <Input.Search
            placeholder="Search students by name..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(val) => setSearchText(val)}
            allowClear
            style={{ marginBottom: 12 }}
            loading={isLoading}
          />
          {isLoading ? (

            <div className="sdp-loader">
              <Spin tip="Looking up students for the selected date and time..." />
            </div>
          ) : availableStudents.length > 0 ? (

            <List
              bordered
              size="small"
              style={{height:"460px", overflow:"auto"}}
              header={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Available students for {selectedDate ? selectedDate.format('DD/MM/YYYY') : 'selected date'}</span>
                  <Tag color="blue">{availableStudentsRaw.length} students</Tag>
                </div>
              }
              dataSource={availableStudentsRaw}
              renderItem={(student: any) => {
                const studentId = getStudentId(student);
                const assignedCount = assignedStudentMap.get(studentId)?.count || 0;
                const totalCapacity = studentCapacityMap.get(studentId) ?? (student.enrollment?.classesRemaining ?? 0);
                const hasCurrentBatchAssignment = assignedCount > 0;
                const disabled = student.canAssign === false && !hasCurrentBatchAssignment;
                const dynamicRemaining = Math.max(totalCapacity - assignedCount, 0);
                const balanceDays = student.enrollment?.found ? dynamicRemaining : 'N/A';
                return (
                  <List.Item
                    actions={[
                      <Button type="link" disabled={disabled} onClick={() => handleStudentSelect(studentId)}>
                        {selectedStudentId === studentId ? 'Selected' : hasCurrentBatchAssignment ? 'Manage' : 'Select'}
                      </Button>,
                    ]}>
                    <List.Item.Meta
                      title={`${student.firstName || ''} ${student.lastName || ''}`.trim() || studentId}
                      description={
                        <span>
                          {student.enrollment?.found ? `Remaining classes: ${balanceDays}` : 'No active enrollment'}
                          {/* {student.conflicts?.length ? ` • ${student.conflicts.length} conflict(s)` : ''} */}
                        </span>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          ) : (
            <Alert message="No available students" description={`No students are available for this date and time slot (${startTime} - ${endTime})`} type="warning" />
          )}
        </div>
        <div className="sdp-calendar" style={{ width: isHorizontalLayout ? '60%' : '100%', marginBottom: 24 }}>
          <Calendar
          fullscreen={false}
          value={calendarMonth}
          onChange={(date) => setCalendarMonth(date)}
          disabledDate={(date) => isDateBlocked(date)}
          headerRender={({ value }) => (
            <div style={{ padding: 8 }}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Text strong>{value.format('MMMM YYYY')}</Text>
                </Col>
                <Col>
                  <Space>
                    <Button type="text" size="small" onClick={() => setCalendarMonth(calendarMonth.subtract(1, 'month'))}>
                      ←
                    </Button>
                    <Button type="text" size="small" onClick={() => setCalendarMonth(calendarMonth.add(1, 'month'))}>
                      →
                    </Button>
                  </Space>
                </Col>
              </Row>
            </div>
          )}
          fullCellRender={(date) => {
            const dateKey = date.format('YYYY-MM-DD');
            const isAssignedSelected = isAssignedToSelectedStudent(date);
            const isAssignedOther = isAssignedToOtherStudent(date);
            const assignments = assignmentsByDate.get(dateKey) || [];
            const isBlocked = isDateBlocked(date);
            const hasAssignments = assignments.length > 0;

            const uniqueAssignments = assignments.filter((a: any, idx: number) =>
              assignments.findIndex((b: any) => getStudentId(b) === getStudentId(a)) === idx
            );

            const tooltipContent = uniqueAssignments.length > 0 ? (
              <div>
                {uniqueAssignments.map((a: any, i: number) => (
                  <div key={i}>{getStudentDisplayName(a)}</div>
                ))}
              </div>
            ) : null;

            const isToday = date.isSame(dayjs(), 'day');

            const assignmentAvatars = uniqueAssignments.slice(0, 2).map((assignment: any, index: number) => {
              const name = getStudentDisplayName(assignment);
              console.log(name);
              const initials = name
                ? name.split(' ').filter(Boolean).map((part: string) => part[0]).join('').slice(0, 2).toUpperCase()
                : 'S';
              return (
                <div key={`${dateKey}-avatar-${index}`} className="sdp-avatar">
                  {initials}
                </div>
              );
            });

            const cellClassName = [
              'sdp-cell',
              isAssignedSelected
                ? 'sdp-cell--selected'
                : isAssignedOther
                ? 'sdp-cell--assigned-other'
                : hasAssignments
                ? 'sdp-cell--assigned'
                : '',
              isToday ? 'sdp-cell--today' : '',
              isBlocked ? 'sdp-cell--blocked' : '',
            ]
              .filter(Boolean)
              .join(' ');

            const cellContent = (
              // title="" suppresses AntD's default native date tooltip (e.g. "2026-07-21")
              <div className={cellClassName} title="" onClick={() => handleDateClick(date)}>
                <div className="sdp-cell__day">{date.date()}</div>
                {assignmentAvatars.length > 0 && (
                  <div className="sdp-cell__avatars">
                    {assignmentAvatars}
                    {uniqueAssignments.length > 2 && (
                      <div className="sdp-avatar sdp-avatar--more">+{uniqueAssignments.length - 2}</div>
                    )}
                  </div>
                )}
              </div>
            );

            return tooltipContent ? (
              <Tooltip title={tooltipContent} placement="top">
                {cellContent}
              </Tooltip>
            ) : cellContent;
          }}
        />
      </div>
      </div>

      {/* <Divider /> */}
    </div>
  );
};


export default StudentDatePicker;
